import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Siren, Camera, AlertTriangle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { todayLocal } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Field, INPUT, URGENCY } from "./maintUtils";

const nowHM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

export function ReportFailureButton({ equipmentId, onDone, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="maint-report-failure-btn" onClick={() => setOpen(true)} className={`rounded-full bg-rose-600 px-5 py-5 text-base font-black uppercase tracking-wide shadow-lg shadow-rose-200 hover:bg-rose-700 ${className}`}>
        <Siren className="mr-2 h-5 w-5" />Signaler une panne
      </Button>
      {open && <ReportFailureDialog equipmentId={equipmentId} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

function ReportFailureDialog({ equipmentId, onClose, onDone }) {
  const { user } = useAuth();
  const [eqs, setEqs] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ equipment_id: equipmentId || "", date: todayLocal(), time: nowHM(), reported_by: user?.name || "", description: "", symptoms: "",
    usable: "non", urgency: "normale", impact: "", comment: "", safety_incident: false, incident_ref: "", auto_status: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/maint/report-options").then((r) => setEqs(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.equipment_id || !f.description.trim()) { toast.error("Choisissez l'appareil et décrivez le problème"); return; }
    setBusy(true);
    try {
      const r = await api.post("/maint/failures", f);
      if (file) {
        const fd = new FormData();
        fd.append("file", file); fd.append("equipment_id", f.equipment_id); fd.append("failure_id", r.data.id);
        fd.append("doc_type", file.type.startsWith("video") ? "video" : "photo"); fd.append("title", `Panne du ${f.date}`);
        await api.post("/maint/documents", fd, { timeout: 180000 }).catch((e) => toast.error(`Panne enregistrée, mais média non envoyé : ${apiErr(e)}`));
      }
      toast.success("Panne enregistrée");
      onClose(); onDone && onDone(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}><DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-lg" data-testid="maint-failure-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl text-rose-700">Signaler une panne</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <Field label="Appareil *">
          <Select value={f.equipment_id || undefined} onValueChange={(v) => set("equipment_id", v)}>
            <SelectTrigger data-testid="maint-failure-equipment" className={INPUT}><SelectValue placeholder="Choisir l'appareil" /></SelectTrigger>
            <SelectContent>{eqs.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.piece ? ` — ${e.piece}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Que se passe-t-il ? *"><Textarea data-testid="maint-failure-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Ex. : le fauteuil ne monte plus" className={INPUT} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="L'appareil est-il utilisable ?">
            <Select value={f.usable} onValueChange={(v) => set("usable", v)}><SelectTrigger data-testid="maint-failure-usable" className={INPUT}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="degrade">Fonctionnement dégradé</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent></Select>
          </Field>
          <Field label="Urgence">
            <Select value={f.urgency} onValueChange={(v) => set("urgency", v)}><SelectTrigger data-testid="maint-failure-urgency" className={INPUT}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(URGENCY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed border-gray-200 p-3 text-sm text-gray-600" data-testid="maint-failure-photo-label">
          <Camera className="h-5 w-5 text-gray-400" />{file ? file.name : "Ajouter une photo ou une vidéo (facultatif)"}
          <input data-testid="maint-failure-photo" type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <details className="rounded-2xl bg-gray-50 p-3 text-sm">
          <summary className="cursor-pointer font-semibold text-gray-600">Plus de détails (facultatif)</summary>
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={INPUT} /></Field>
              <Field label="Heure"><Input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} className={INPUT} /></Field>
            </div>
            <Field label="Signalé par"><Input value={f.reported_by} onChange={(e) => set("reported_by", e.target.value)} className={INPUT} /></Field>
            <Field label="Symptômes"><Input value={f.symptoms} onChange={(e) => set("symptoms", e.target.value)} className={INPUT} /></Field>
            <Field label="Impact sur l'activité"><Input value={f.impact} onChange={(e) => set("impact", e.target.value)} placeholder="Ex. : cabinet 2 inutilisable" className={INPUT} /></Field>
            <Field label="Commentaire"><Input value={f.comment} onChange={(e) => set("comment", e.target.value)} className={INPUT} /></Field>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.auto_status} onChange={(e) => set("auto_status", e.target.checked)} className="accent-[#065F46]" /> Mettre à jour le statut de l'appareil automatiquement</label>
            <label className="flex items-center gap-2"><input data-testid="maint-failure-safety" type="checkbox" checked={f.safety_incident} onChange={(e) => set("safety_incident", e.target.checked)} className="accent-rose-600" /> Incident potentiellement lié à la sécurité du dispositif</label>
            {f.safety_incident && (
              <div className="space-y-2 rounded-xl bg-amber-50 p-2 text-amber-800" data-testid="maint-vigilance-alert">
                <p className="flex items-center gap-1.5 text-xs font-semibold"><AlertTriangle className="h-4 w-4" />Vérifier si cet événement relève d'une procédure de matériovigilance.</p>
                <Input value={f.incident_ref} onChange={(e) => set("incident_ref", e.target.value)} placeholder="Identifiant interne d'incident (jamais le nom d'un patient)" className="rounded-xl bg-white" />
              </div>
            )}
          </div>
        </details>
        <Button data-testid="maint-failure-submit" disabled={busy} onClick={submit} className="w-full rounded-full bg-rose-600 py-6 font-bold hover:bg-rose-700">{busy ? "Enregistrement…" : "Enregistrer la panne"}</Button>
      </div>
    </DialogContent></Dialog>
  );
}
